import React, { useEffect, useRef, useState } from "react";
import "./login.css";
import { DocketAPIError, getAPIClient, legalSexFromString } from "../../apiClient";
import { useNavigate } from "react-router";
import { useForm, SubmitHandler } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { errorLog, infoLog } from "../../utils/logger";
import ResponsiveDatePicker from "../../components/form/ResponsiveDatePicker";
import { FormFieldset } from "../../components/form/FormFieldset";
import {
  checkBoxValueMap,
  getLocalizedLegalSexChoices,
  getSearchRadios,
} from "../../utils/formUtils";
import { DateTime } from "luxon";
import lodash from "lodash";
import { ErrorModal } from "../../components/modals/ErrorModal";
import { useAtom } from "jotai";
import { biometricAuthAtom, userAtom } from "../../jotai/atoms";
import { BiometricAuth, BiometryType } from "@aparajita/capacitor-biometric-auth";
import { Capacitor } from "@capacitor/core";
import { getBiometricLabel } from "../../utils/biometrics";
import { registerPushPermission } from "../../pushNotifications";
import { vibrateDevice } from "../../utils/capacitor";

export function AccountInfo() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [dob, setDob] = useState<Date | null>(null);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [showErrorMessage, setShowErrorMessage] = useState<boolean | string>(false);
  const [checkboxValue, setCheckboxValue] = useState(getLocalizedLegalSexChoices(t));
  const [biometricAuth, setBiometricAuth] = useAtom(biometricAuthAtom);
  const [biometryType, setBiometryType] = useState<BiometryType | null>(null);
  const [pushPermission, setPushPermission] = useState<boolean>(false);
  const lastNameRef = useRef<HTMLInputElement>(null);

  const checkboxHandler = (_value: any, index: number) => {
    const newValue = checkBoxValueMap(checkboxValue, index);
    infoLog(`newValue ${JSON.stringify(newValue)}`);
    setCheckboxValue(newValue);
  };

  const [currentUser, setCurrentUser] = useAtom(userAtom);

  const registerNativeSettings = async () => {
    const biometry = await BiometricAuth.checkBiometry();
    if (biometry.isAvailable) {
      setBiometryType(biometry.biometryType);
    }

    const pushPermission = await registerPushPermission();
    if (pushPermission) {
      setPushPermission(true);
    }
  };

  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      registerNativeSettings();
    }
  }, []);

  useEffect(() => {
    if (currentUser?.dob) {
      const dobDateTime = DateTime.fromISO(currentUser.dob);
      if (dobDateTime.isValid) {
        setDob(dobDateTime.toJSDate());
      }
    }
    if (currentUser?.first_name) {
      setFirstName(currentUser.first_name);
    }
    if (currentUser?.last_name) {
      setLastName(currentUser.last_name);
    }
    if (currentUser?.legal_sex) {
      checkboxHandler(currentUser.legal_sex, 0);
    }
  }, [currentUser]);

  const toggleBiometricAuth = async () => {
    if (biometryType && !biometricAuth) {
      try {
        await BiometricAuth.authenticate();
        await vibrateDevice();
        setBiometricAuth(true);
      } catch (error) {
        setBiometricAuth(false);
      }
    } else {
      setBiometricAuth(false);
    }
  };

  const { handleSubmit } = useForm();

  const searchFormTranslations = {
    dateOfBirth: t("immunizations.search_my_dob_label"),
    legalSex: t("immunizations.search_my_legal_sex_label"),
    firstName: t("onboarding.first-name"),
    lastName: t("onboarding.last-name"),
  };

  const ageCheckPassed = () => {
    if (dob && DateTime.now().minus({ years: 13 }) <= DateTime.fromJSDate(dob)) {
      return false;
    }
    return true;
  };

  const onSubmit = async (data: any) => {
    if (!ageCheckPassed()) {
      errorLog({ message: t("settings.dob_error_message") } as Error);
      setShowErrorMessage(`${t("settings.dob_error_message")}`);
      return;
    }

    try {
      let sex = lodash.find(checkboxValue, ["checked", true]);
      let legalSex = sex?.value ?? "U";
      const params = {
        first_name: firstName,
        last_name: lastName,
        legal_sex: legalSex,
        dob: dob ? DateTime.fromJSDate(dob).toISODate() : null,
        language: "en",
        notification_preferences: {
          email_new_results: true,
          email_upcoming_vaccines: true,
          email_app_updates: true,
          push_new_results: pushPermission,
          push_upcoming_vaccines: pushPermission,
          push_app_updates: pushPermission,
        },
      };
      const res = await getAPIClient().updateUser(params);
      // save user info to db
      if (res) {
        await setCurrentUser({
          ...currentUser!,
          legal_sex: legalSexFromString(legalSex),
          dob: dob ? DateTime.fromJSDate(dob).toLocaleString() : null,
          first_name: firstName,
          last_name: lastName,
          language: "en",
          notification_preferences: {
            ...params.notification_preferences,
            disable_all: false,
          },
        });
        navigate("../verifyphone");
      }
    } catch (e: unknown) {
      errorLog(e as Error);
      if (e instanceof DocketAPIError) {
        setShowErrorMessage(e.message);
      }
    }
  };

  const handleKeyDownFirstName = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      setTimeout(() => {
        lastNameRef.current?.focus();
      }, 50);
    }
  };

  const handleKeyDownLastName = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      lastNameRef.current?.blur();
    }
  };

  const isButtonDisabled =
    firstName.length < 2 ||
    lastName.length < 2 ||
    !dob ||
    !lodash.find(checkboxValue, ["checked", true]);

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} className="h-full flex flex-col">
        {showErrorMessage && (
          <ErrorModal onCloseModal={() => setShowErrorMessage(false)}>
            {showErrorMessage}
          </ErrorModal>
        )}
        <div className="flex-1 px-2">
          <div className="flex flex-col gap-2">
            <FormFieldset label={searchFormTranslations.firstName} required>
              <input
                className="input input-primary max-md:text-lg w-full"
                type="text"
                id={"firstName"}
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder={searchFormTranslations.firstName}
                onKeyDown={handleKeyDownFirstName}
              />
            </FormFieldset>
            <FormFieldset label={searchFormTranslations.lastName} required>
              <input
                className="input input-primary max-md:text-lg w-full"
                id={"lastName"}
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder={searchFormTranslations.lastName}
                ref={lastNameRef}
                onKeyDown={handleKeyDownLastName}
              />
            </FormFieldset>
            <FormFieldset label={searchFormTranslations.dateOfBirth} required>
              <ResponsiveDatePicker
                key="dateOfBirth"
                selectedDate={dob}
                setSelectedDate={(d) => setDob(d)}
                id="dateOfBirth"
              />
            </FormFieldset>
            {getSearchRadios(
              checkboxValue,
              searchFormTranslations.legalSex,
              checkboxHandler,
              "",
              "gender",
              t
            )}
            {biometryType && (
              <label className="flex items-center gap-4 cursor-pointer">
                <input
                  type="checkbox"
                  className="toggle toggle-primary"
                  checked={biometricAuth}
                  onChange={toggleBiometricAuth}
                  data-testid="biometric-auth-toggle"
                />
                <span>Use {getBiometricLabel(biometryType)}</span>
              </label>
            )}
            {/* Desktop button stays in content flow */}
            <button
              className="btn btn-primary btn-lg w-full max-md:hidden"
              type="submit"
              disabled={isButtonDisabled}
            >
              {t("generic.continue")}
            </button>
          </div>
        </div>
        {/* Mobile button anchored at bottom */}
        <div className="shrink-0 max-md:pb-8 md:hidden pt-4 px-2">
          <button
            className="btn btn-primary btn-lg w-full"
            type="submit"
            disabled={isButtonDisabled}
          >
            {t("generic.continue")}
          </button>
        </div>
      </form>
    </>
  );
}

export default AccountInfo;
